import { useEffect, useMemo, useState } from 'react'
import {
  Users,
  Mail,
  UserPlus,
  CheckCircle,
  XCircle,
  Clock,
  UserMinus,
  UserCog,
  Shield,
  Copy,
  Loader2,
  Ban
} from 'lucide-react'

import {
  organizationApi,
  type InviteStatus,
  type TeamInvite,
  type TeamJoinRequest,
  type TeamMember,
  type TeamOverview
} from '@/services/organization-api'
import { useOrgStore } from '@/stores/org-store'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'

function statusVariant(status: InviteStatus): 'default' | 'secondary' | 'outline' | 'destructive' {
  switch (status) {
    case 'pending':
      return 'default'
    case 'accepted':
      return 'secondary'
    case 'expired':
      return 'outline'
    case 'revoked':
      return 'destructive'
    default:
      return 'outline'
  }
}

function formatDate(value?: string | null): string {
  if (!value) return '-'
  return new Date(value).toLocaleString()
}

export default function TeamPage() {
  const org = useOrgStore((s) => s.currentOrg)
  const [overview, setOverview] = useState<TeamOverview | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmittingInvite, setIsSubmittingInvite] = useState(false)
  const [email, setEmail] = useState('')
  const [inviteRole, setInviteRole] = useState('member')
  const [lastToken, setLastToken] = useState('')
  const [copied, setCopied] = useState(false)

  const canManage = useMemo(
    () => org?.role === 'owner' || org?.role === 'admin',
    [org?.role]
  )

  const load = async () => {
    if (!org) return
    setIsLoading(true)
    try {
      const data = await organizationApi.getTeamOverview(org.id)
      setOverview(data)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [org?.id])

  const createInvite = async () => {
    if (!org || !canManage) return
    setIsSubmittingInvite(true)
    try {
      const invite = await organizationApi.createInvite(
        org.id,
        email.trim() || undefined,
        inviteRole,
        7 // Fixed to 7 days
      )
      setLastToken(invite.invite_token || '')
      setEmail('')
      await load()
    } finally {
      setIsSubmittingInvite(false)
    }
  }

  const copyToken = async () => {
    if (!lastToken) return
    await navigator.clipboard.writeText(lastToken)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const changeRole = async (userId: string, role: string) => {
    if (!org || !canManage) return
    await organizationApi.updateMemberRole(org.id, userId, role)
    await load()
  }

  const removeMember = async (userId: string) => {
    if (!org || !canManage) return
    await organizationApi.removeMember(org.id, userId)
    await load()
  }

  const revokeInvite = async (inviteId: string) => {
    if (!org || !canManage) return
    await organizationApi.revokeInvite(org.id, inviteId)
    await load()
  }

  const approveJoinRequest = async (requestId: string) => {
    if (!org || !canManage) return
    await organizationApi.approveJoinRequest(org.id, requestId)
    await load()
  }

  const rejectJoinRequest = async (requestId: string) => {
    if (!org || !canManage) return
    await organizationApi.rejectJoinRequest(org.id, requestId)
    await load()
  }

  if (!org) {
    return (
      <Alert>
        <AlertDescription>Select an organization first.</AlertDescription>
      </Alert>
    )
  }

  const members = overview?.members || []
  const invites = overview?.invites || []
  const joinRequests = overview?.join_requests || []

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Team</h1>
        <p className="mt-1 text-muted-foreground">Manage members, invitations, and join requests.</p>
      </div>

      {!canManage && (
        <Alert>
          <AlertDescription>
            You have read-only access to team settings. Ask an owner or admin to make changes.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Invite Users</CardTitle>
          <CardDescription>Create a shareable invite token for your team.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="invite-email">Email (optional)</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={!canManage || isSubmittingInvite}
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={inviteRole} onValueChange={setInviteRole} disabled={!canManage || isSubmittingInvite}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="text-sm text-muted-foreground">
            Expires in 7 days
          </div>

          <Button onClick={createInvite} disabled={!canManage || isSubmittingInvite}>
            {isSubmittingInvite ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <UserPlus className="mr-2 h-4 w-4" />
                Create Invite
              </>
            )}
          </Button>

          {lastToken && (
            <Alert>
              <AlertDescription className="flex items-center justify-between">
                <span className="font-mono text-sm truncate mr-2">Token: {lastToken}</span>
                <Button variant="outline" size="sm" onClick={copyToken}>
                  {copied ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Invitations
          </CardTitle>
          <CardDescription>{invites.length} invitation{invites.length !== 1 ? 's' : ''}</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2].map((i) => (
                <div key={i} className="h-16 rounded-lg bg-muted animate-pulse" />
              ))}
            </div>
          ) : invites.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No invitations yet</div>
          ) : (
            <div className="space-y-3">
              {invites.map((invite: TeamInvite) => (
                <div
                  key={invite.id}
                  className="flex items-center justify-between p-4 rounded-lg border border-border bg-card"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-foreground">{invite.email || 'No email specified'}</p>
                      <Badge variant={statusVariant(invite.status)}>{invite.status}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">Created: {formatDate(invite.created_at)}</p>
                    <p className="text-sm text-muted-foreground">Expires: {formatDate(invite.expires_at)}</p>
                    <p className="text-sm text-muted-foreground">Accepted: {formatDate(invite.accepted_at)}</p>
                  </div>
                  {canManage && invite.status === 'pending' && (
                    <Button variant="outline" size="sm" onClick={() => revokeInvite(invite.id)}>
                      <Ban className="mr-2 h-4 w-4" />
                      Revoke
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Members
          </CardTitle>
          <CardDescription>{members.length} member{members.length !== 1 ? 's' : ''}</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 rounded-lg bg-muted animate-pulse" />
              ))}
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No members found</div>
          ) : (
            <div className="space-y-3">
              {members.map((member: TeamMember) => (
                <div
                  key={member.id}
                  className="flex items-center justify-between p-4 rounded-lg border border-border bg-card hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Users className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{member.full_name || member.user_id}</p>
                      {member.full_name && <p className="text-sm text-muted-foreground">{member.user_id}</p>}
                      <Badge className="mt-1" variant={member.role === 'owner' || member.role === 'admin' ? 'default' : 'secondary'}>
                        {member.role}
                      </Badge>
                    </div>
                  </div>
                  {canManage && member.role !== 'owner' && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm">
                          <UserCog className="h-4 w-4 mr-2" />
                          Actions
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Change Role</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => changeRole(member.user_id, 'member')}>
                          <Users className="h-4 w-4 mr-2" />
                          Set as Member
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => changeRole(member.user_id, 'admin')}>
                          <Shield className="h-4 w-4 mr-2" />
                          Set as Admin
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => removeMember(member.user_id)}
                          className="text-destructive focus:text-destructive"
                        >
                          <UserMinus className="h-4 w-4 mr-2" />
                          Remove Member
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Join Requests
          </CardTitle>
          <CardDescription>{joinRequests.length} pending request{joinRequests.length !== 1 ? 's' : ''}</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2].map((i) => (
                <div key={i} className="h-16 rounded-lg bg-muted animate-pulse" />
              ))}
            </div>
          ) : joinRequests.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No pending join requests</div>
          ) : (
            <div className="space-y-3">
              {joinRequests.map((item: TeamJoinRequest) => (
                <div key={item.id} className="flex items-center justify-between p-4 rounded-lg border border-border bg-card">
                  <div>
                    <p className="font-medium text-foreground">{item.user_id}</p>
                    <p className="text-sm text-muted-foreground">{item.message || 'No message provided'}</p>
                  </div>
                  {canManage && (
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => approveJoinRequest(item.id)}>
                        <CheckCircle className="mr-2 h-4 w-4" />
                        Approve
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => rejectJoinRequest(item.id)}>
                        <XCircle className="mr-2 h-4 w-4" />
                        Reject
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
